import { Subscribable, SubscriberId, Subscription } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { OptionChainRequest } from './request';
import { OptionChainResponse } from './entities';
import { SafeError, SafePromise } from '@benzinga/safe-await';

interface OptionChainEvent {
  type: 'option-chain:event' | 'option-chain:getData' | 'optionChain_event' | 'option-chain:error';
  data?: OptionChainResponse;
  error?: SafeError;
  // The symbols the subscription requested, so listeners can ignore other subscriptions' results.
  symbols?: string | string[];
}

export class OptionChainManager extends Subscribable<OptionChainEvent> {
  private request: OptionChainRequest;
  private session: Session;
  private restfulSubscription?: Subscription<OptionChainRequest>;
  private intervalId?: any;

  constructor(session: Session) {
    super();
    this.request = new OptionChainRequest(session);
    this.session = session;
  }

  public static getName = () => 'benzinga-option-chain';

  public getOptionChainData = (symbols: string | string[]): SafePromise<OptionChainResponse> => {
    return this.request.getOptionChain(symbols);
  };

  public getOptionChainSubscription = (symbols: string | string[], interval: number) => {
    const fetchAndDispatch = () =>
      this.request.getOptionChain(symbols).then(res => {
        if (res.err) {
          this.dispatch({ error: res.err, symbols, type: 'option-chain:error' });
        } else {
          this.dispatch({ data: res.ok, symbols, type: 'option-chain:event' });
        }
      });

    fetchAndDispatch();

    const subscription = this.subscribe(event => {
      if (event.type === 'option-chain:getData') {
        fetchAndDispatch();
      }
    });

    this.intervalId = setInterval(() => {
      this.dispatch({ type: 'option-chain:getData' });
    }, interval);

    return subscription;
  };

  protected onUnsubscribe(_id: SubscriberId) {
    clearInterval(this.intervalId);
  }

  protected override onFirstSubscription(): void {
    this.restfulSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected override onZeroSubscriptions(): void {
    this.restfulSubscription?.unsubscribe();
  }
}
